import { Permission } from './permission.js'
import { Role } from './role.js'

export type RolePermissions = {
  /**
   * The name of the role the permissions are associated with.
   */
  role: Role

  /**
   * The list of the permission for this role.
   */
  permissions: Permission[]
}

/**
 * Our static definition of roles for each permission.
 */
export const DroidMapRolePermissions: Map<Role, RolePermissions> = new Map([
  [
    Role.ORG_ADMIN,
    {
      role: Role.ORG_ADMIN,
      permissions: [
        Permission.LIST_ORGANIZATIONS,
        Permission.VIEW_ORGANIZATION,
        Permission.UPDATE_ORGANIZATION,
        Permission.DELETE_ORGANIZATION,
        Permission.CREATE_ORGANISATION_USER,
        Permission.LIST_ORGANISATION_USERS,
        Permission.VIEW_ORGANISATION_USER,
        Permission.UPDATE_ORGANISATION_USER,
        Permission.DELETE_ORGANISATION_USER,
        Permission.CREATE_GROUP,
        Permission.LIST_GROUPS,
        Permission.VIEW_GROUP,
        Permission.UPDATE_GROUP,
        Permission.DELETE_GROUP,
        Permission.CREATE_GROUP_USER,
        Permission.LIST_GROUP_USERS,
        Permission.VIEW_GROUP_USER,
        Permission.DELETE_GROUP_USER,
        Permission.CREATE_PROJECT,
        Permission.LIST_PROJECTS,
        Permission.VIEW_PROJECT,
        Permission.UPDATE_PROJECT,
        Permission.DELETE_PROJECT,
        Permission.CREATE_TASK,
        Permission.LIST_TASKS,
        Permission.VIEW_TASK,
        Permission.UPDATE_TASK,
        Permission.DELETE_TASK,
        Permission.CREATE_ORG_GRANT,
        Permission.CREATE_PROJECT_GRANT,
        Permission.LIST_GRANTS,
        Permission.VIEW_GRANT,
        Permission.VIEW_RESOURCES_WITH_PERMISSION,
        Permission.DELETE_ORG_GRANT,
        Permission.DELETE_PROJECT_GRANT,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.CREATE_ASSET,
        Permission.LIST_ASSETS,
        Permission.VIEW_ASSET,
        Permission.UPDATE_ASSET,
        Permission.DELETE_ASSET,
        Permission.CREATE_ASSET_SET,
        Permission.LIST_ASSET_SETS,
        Permission.VIEW_ASSET_SET,
        Permission.UPDATE_ASSET_SET,
        Permission.DELETE_ASSET_SET,
        Permission.CREATE_GEOMETRY,
        Permission.LIST_GEOMETRIES,
        Permission.VIEW_GEOMETRY,
        Permission.UPDATE_GEOMETRY,
        Permission.DELETE_GEOMETRY,
      ],
    },
  ],
  [
    Role.ORG_WRITE,
    {
      role: Role.ORG_WRITE,
      permissions: [
        Permission.VIEW_ORGANIZATION,
        Permission.LIST_ORGANIZATIONS,
        Permission.CREATE_PROJECT,
        Permission.LIST_PROJECTS,
        Permission.VIEW_RESOURCES_WITH_PERMISSION,
        Permission.VIEW_GRANT,
        Permission.LIST_GRANTS,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.CREATE_ORGANISATION_USER,
        Permission.LIST_ORGANISATION_USERS,
        Permission.VIEW_ORGANISATION_USER,
        Permission.UPDATE_ORGANISATION_USER,
        Permission.DELETE_ORGANISATION_USER,
        Permission.CREATE_GROUP,
        Permission.LIST_GROUPS,
        Permission.VIEW_GROUP,
        Permission.UPDATE_GROUP,
        Permission.DELETE_GROUP,
        Permission.CREATE_GROUP_USER,
        Permission.LIST_GROUP_USERS,
        Permission.VIEW_GROUP_USER,
        Permission.DELETE_GROUP_USER,
      ],
    },
  ],
  [
    Role.ORG_READ,
    {
      role: Role.ORG_READ,
      permissions: [
        Permission.VIEW_ORGANIZATION,
        Permission.LIST_ORGANIZATIONS,
        Permission.LIST_PROJECTS,
        Permission.VIEW_RESOURCES_WITH_PERMISSION,
        Permission.LIST_GRANTS,
        Permission.VIEW_GRANT,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.LIST_ORGANISATION_USERS,
        Permission.VIEW_ORGANISATION_USER,
        Permission.LIST_GROUPS,
        Permission.VIEW_GROUP,
        Permission.LIST_GROUP_USERS,
        Permission.VIEW_GROUP_USER,
      ],
    },
  ],
  [
    Role.PROJECT_ADMIN,
    {
      role: Role.PROJECT_ADMIN,
      permissions: [
        Permission.VIEW_PROJECT,
        Permission.UPDATE_PROJECT,
        Permission.DELETE_PROJECT,
        Permission.CREATE_TASK,
        Permission.LIST_TASKS,
        Permission.VIEW_TASK,
        Permission.UPDATE_TASK,
        Permission.DELETE_TASK,
        Permission.CREATE_PROJECT_GRANT,
        Permission.LIST_GRANTS,
        Permission.VIEW_GRANT,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.DELETE_PROJECT_GRANT,
        Permission.CREATE_ASSET,
        Permission.LIST_ASSETS,
        Permission.VIEW_ASSET,
        Permission.UPDATE_ASSET,
        Permission.DELETE_ASSET,
        Permission.CREATE_ASSET_SET,
        Permission.LIST_ASSET_SETS,
        Permission.VIEW_ASSET_SET,
        Permission.UPDATE_ASSET_SET,
        Permission.DELETE_ASSET_SET,
        Permission.CREATE_GEOMETRY,
        Permission.LIST_GEOMETRIES,
        Permission.VIEW_GEOMETRY,
        Permission.UPDATE_GEOMETRY,
        Permission.DELETE_GEOMETRY,
      ],
    },
  ],
  [
    Role.PROJECT_WRITE,
    {
      role: Role.PROJECT_WRITE,
      permissions: [
        Permission.VIEW_PROJECT,
        Permission.UPDATE_PROJECT,
        Permission.CREATE_TASK,
        Permission.LIST_TASKS,
        Permission.VIEW_TASK,
        Permission.UPDATE_TASK,
        Permission.DELETE_TASK,
        Permission.LIST_GRANTS,
        Permission.VIEW_GRANT,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.CREATE_ASSET,
        Permission.LIST_ASSETS,
        Permission.VIEW_ASSET,
        Permission.UPDATE_ASSET,
        Permission.DELETE_ASSET,
        Permission.CREATE_ASSET_SET,
        Permission.LIST_ASSET_SETS,
        Permission.VIEW_ASSET_SET,
        Permission.UPDATE_ASSET_SET,
        Permission.DELETE_ASSET_SET,
        Permission.CREATE_GEOMETRY,
        Permission.LIST_GEOMETRIES,
        Permission.VIEW_GEOMETRY,
        Permission.UPDATE_GEOMETRY,
        Permission.DELETE_GEOMETRY,
      ],
    },
  ],
  [
    Role.PROJECT_READ,
    {
      role: Role.PROJECT_READ,
      permissions: [
        Permission.VIEW_PROJECT,
        Permission.LIST_TASKS,
        Permission.VIEW_TASK,
        Permission.LIST_GRANTS,
        Permission.VIEW_GRANT,
        Permission.LIST_USER_GRANTS,
        Permission.VIEW_USER_GRANT,
        Permission.LIST_ASSETS,
        Permission.VIEW_ASSET,
        Permission.LIST_ASSET_SETS,
        Permission.VIEW_ASSET_SET,
        Permission.LIST_GEOMETRIES,
        Permission.VIEW_GEOMETRY,
      ],
    },
  ],
])

/* ------------------ Reverse Lookup of Roles by Permission ----------------- */

let droidMapRolesByPermission: Map<Permission, Set<Role>>

const initRolesByPermission = () => {
  droidMapRolesByPermission = new Map()
  for (const [role, rolePermission] of DroidMapRolePermissions) {
    for (const permission of rolePermission.permissions) {
      const roles = droidMapRolesByPermission.get(permission) || new Set()
      roles.add(role)
      droidMapRolesByPermission.set(permission, roles)
    }
  }
}

export const getRolesWithPermission = (permission: Permission): Set<Role> => {
  if (!droidMapRolesByPermission) initRolesByPermission()

  return droidMapRolesByPermission.get(permission) || new Set()
}
